import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  Thermometer, Droplets, Wind, Gauge, Snowflake, Sprout, CloudRain,
  Fish, Crosshair, TrendingUp, TrendingDown, ArrowLeft, Calendar,
  ThumbsUp, ThumbsDown, BarChart3, Target, Leaf, Clock
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyLandPage() {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [dejaVu, setDejaVu] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  const lat = searchParams.get("lat") || user?.preferences?.default_location?.lat;
  const lon = searchParams.get("lon") || user?.preferences?.default_location?.lon;
  const locationName = searchParams.get("name") || user?.preferences?.default_location?.name || "Home";

  useEffect(() => {
    if (!token) return;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/api/subscription/status`, { headers: { Authorization: `Bearer ${token}` } });
        setIsPremium(res.data?.is_premium);
      } catch (err) { console.error("Sub check error:", err.message); }
    };
    check();
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!lat || !lon || !token) return;
    setLoading(true);
    try {
      // Capture snapshot first (auto, idempotent)
      await axios.post(`${API}/api/intelligence/snapshot?lat=${lat}&lon=${lon}&location_name=${encodeURIComponent(locationName)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const [landRes, dvRes, tlRes] = await Promise.all([
        axios.get(`${API}/api/intelligence/my-land`, { params: { lat, lon, location_name: locationName }, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/intelligence/deja-vu`, { params: { lat, lon, location_name: locationName }, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/intelligence/timeline`, { params: { location_name: locationName, months: 6 }, headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(landRes.data);
      setDejaVu(dvRes.data);
      setTimeline(tlRes.data);
    } catch (err) {
      console.error("My Land fetch error:", err.message);
      toast.error("Failed to load My Land data");
    } finally {
      setLoading(false);
    }
  }, [lat, lon, locationName, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitFeedback = useCallback(async (accurate) => {
    try {
      await axios.post(`${API}/api/intelligence/feedback`, { location_name: locationName, accurate }, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbackGiven(true);
      toast.success(accurate ? "Glad it was accurate!" : "Thanks — this improves predictions");
    } catch (err) { toast.error("Failed to save feedback"); }
  }, [locationName, token]);

  if (!isPremium) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-[#050505] text-white" : "bg-gray-50 text-gray-900"}`}>
        <div className="text-center p-8">
          <Target className="w-12 h-12 mx-auto mb-4 text-[#00f2ea]" />
          <h2 className="text-2xl font-bold mb-2">My Tracker</h2>
          <p className="text-white/50 mb-4">Upgrade to Premium to unlock microclimate insights for your locations.</p>
          <Link to="/subscription" className="bg-[#00f2ea] text-black font-bold px-6 py-3 rounded-xl">Upgrade to Premium</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-[#050505]" : "bg-gray-50"}`}>
        <div className="animate-spin w-8 h-8 border-2 border-[#00f2ea] border-t-transparent rounded-full" />
      </div>
    );
  }

  const frost = data?.frost_probability || {};
  const gdd = data?.growing_degree_days || {};
  const rain = data?.rain_analysis || {};
  const yoy = data?.year_over_year || {};
  const activities = data?.activity_windows || {};
  const accuracy = data?.forecast_accuracy || {};

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-[#050505] text-white" : "bg-gray-50 text-gray-900"}`}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/" className={`p-2 rounded-xl ${isDarkMode ? "hover:bg-white/10" : "hover:bg-gray-200"}`}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" data-testid="my-land-title">My Tracker</h1>
            <p className={`text-sm ${isDarkMode ? "text-white/40" : "text-gray-500"}`}>
              {locationName} — {data?.total_days_tracked || 0} days tracked
            </p>
          </div>
        </div>

        {/* Forecast Feedback Bar */}
        {!feedbackGiven && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="forecast-feedback"
            className={`flex items-center justify-between p-4 rounded-2xl mb-6 ${isDarkMode ? "bg-white/5 border border-white/10" : "bg-white border border-gray-200"}`}
          >
            <div>
              <p className="text-sm font-semibold">Was today&apos;s forecast accurate?</p>
              <p className={`text-xs ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>Your feedback improves predictions for this location</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => submitFeedback(true)} data-testid="feedback-thumbsup"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-semibold transition-colors">
                <ThumbsUp className="w-4 h-4" /> Yes
              </button>
              <button onClick={() => submitFeedback(false)} data-testid="feedback-thumbsdown"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm font-semibold transition-colors">
                <ThumbsDown className="w-4 h-4" /> No
              </button>
            </div>
          </motion.div>
        )}

        {/* Top Row: Activity Windows */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { key: "fishing", label: "Fishing", icon: Fish, color: "text-emerald-400", bg: "bg-emerald-500/10" },
            { key: "hunting", label: "Hunting", icon: Crosshair, color: "text-amber-400", bg: "bg-amber-500/10" },
            { key: "gardening", label: "Gardening", icon: Leaf, color: "text-green-400", bg: "bg-green-500/10" },
          ].map(({ key, label, icon: Icon, color, bg }) => {
            const w = activities[key] || {};
            return (
              <motion.div key={`activity-${key}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`activity-${key}`}
                className={`p-5 rounded-2xl ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-2 rounded-xl ${bg}`}><Icon className={`w-5 h-5 ${color}`} /></div>
                  <span className="font-bold text-sm">{label}</span>
                  <span className={`ml-auto text-2xl font-black ${color}`}>{w.score || 0}<span className="text-xs font-normal text-white/30">/10</span></span>
                </div>
                <p className={`text-xs font-semibold mb-1 ${color}`}>{w.rating || "N/A"}</p>
                <p className={`text-xs ${isDarkMode ? "text-white/40" : "text-gray-500"}`}>{w.tip || ""}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Déjà Vu Section */}
        {dejaVu && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="deja-vu-section"
            className={`p-5 rounded-2xl mb-6 ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#00f2ea]" /> Weather Deja Vu
            </h3>
            {dejaVu.current && (
              <div className={`flex items-center gap-4 p-3 rounded-xl mb-3 ${isDarkMode ? "bg-white/5" : "bg-gray-50"}`}>
                <span className="text-sm font-semibold">Now:</span>
                <span className="text-sm">{dejaVu.current.temp}°F</span>
                <span className={`text-xs ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>{dejaVu.current.description}</span>
                <span className="text-xs text-white/30">Wind {dejaVu.current.wind_speed}mph</span>
                <span className="text-xs text-white/30">{dejaVu.current.humidity}% humidity</span>
              </div>
            )}
            {dejaVu.matches?.length > 0 ? (
              <div className="space-y-2">
                {dejaVu.matches.map((m, index) => (
                  <div key={`match-${m.date}-${index}`} className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? "bg-white/[0.03]" : "bg-gray-50"}`}>
                    <div>
                      <span className="text-sm font-semibold">{new Date(m.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      <span className={`text-xs ml-2 ${isDarkMode ? "text-white/40" : "text-gray-400"}`}>
                        {m.conditions.temp}°F, {m.conditions.description}
                      </span>
                    </div>
                    <span className={`text-sm font-bold px-3 py-1 rounded-lg ${m.similarity >= 90 ? "bg-emerald-500/20 text-emerald-400" : m.similarity >= 80 ? "bg-blue-500/20 text-blue-400" : "bg-white/10 text-white/60"}`}>
                      {m.similarity}% match
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>
                {data?.total_days_tracked < 7 ? "Keep visiting — patterns appear after a week of data" : "No strong matches found for today's conditions"}
              </p>
            )}
            {/* Insights */}
            {dejaVu.insights?.length > 0 && (
              <div className="mt-3 space-y-2">
                {dejaVu.insights.map((insight, idx) => (
                  <div key={`insight-${insight.type}`} className={`flex items-center gap-2 text-xs p-2 rounded-lg ${isDarkMode ? "bg-[#00f2ea]/5 text-[#00f2ea]/80" : "bg-cyan-50 text-cyan-700"}`}>
                    <Thermometer className="w-3 h-3 flex-shrink-0" />
                    {insight.text}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Frost Probability */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            data-testid="frost-card"
            className={`p-5 rounded-2xl ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center gap-2 mb-3">
              <Snowflake className="w-4 h-4 text-cyan-300" />
              <span className="text-sm font-bold">Frost Tonight</span>
            </div>
            <div className="text-3xl font-black text-cyan-300">{frost.probability || 0}%</div>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>{frost.message || ""}</p>
          </motion.div>

          {/* Growing Degree Days */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            data-testid="gdd-card"
            className={`p-5 rounded-2xl ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center gap-2 mb-3">
              <Sprout className="w-4 h-4 text-green-400" />
              <span className="text-sm font-bold">Growing Degree Days</span>
            </div>
            <div className="text-3xl font-black text-green-400">{gdd.total || 0}</div>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>Base 50°F — {gdd.days_tracked || 0} days tracked</p>
          </motion.div>

          {/* Rain Analysis */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            data-testid="rain-card"
            className={`p-5 rounded-2xl ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center gap-2 mb-3">
              <CloudRain className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-bold">Rain Pattern</span>
            </div>
            <div className="text-3xl font-black text-blue-400">{rain.rain_percentage || 0}%</div>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>
              {rain.rain_days_last_30 || 0} rain days / {rain.current_dry_streak || 0} day dry streak
            </p>
          </motion.div>

          {/* Forecast Accuracy */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            data-testid="accuracy-card"
            className={`p-5 rounded-2xl ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-[#00f2ea]" />
              <span className="text-sm font-bold">Forecast Accuracy</span>
            </div>
            <div className="text-3xl font-black text-[#00f2ea]">{accuracy.accuracy !== null ? `${accuracy.accuracy}%` : "—"}</div>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>{accuracy.message || ""}</p>
          </motion.div>
        </div>

        {/* Year over Year */}
        {yoy.available && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            data-testid="yoy-card"
            className={`p-5 rounded-2xl mb-6 ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#ccff00]" /> Year-over-Year
            </h3>
            <div className="flex items-center gap-6">
              <div>
                <p className={`text-xs ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>This month avg</p>
                <p className="text-xl font-bold">{yoy.this_month_avg}°F</p>
              </div>
              {yoy.last_year_avg && (
                <>
                  <div>
                    <p className={`text-xs ${isDarkMode ? "text-white/30" : "text-gray-400"}`}>Last year avg</p>
                    <p className="text-xl font-bold">{yoy.last_year_avg}°F</p>
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-bold ${yoy.temp_diff > 0 ? "text-red-400" : "text-blue-400"}`}>
                    {yoy.temp_diff > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {yoy.comparison}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Timeline */}
        {timeline?.timeline?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            data-testid="timeline-section"
            className={`p-5 rounded-2xl ${isDarkMode ? "bg-white/5 border border-white/5" : "bg-white border border-gray-200"}`}>
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-white/40" /> Monthly Timeline
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {timeline.timeline.map((m) => (
                <div key={`month-${m.month}`} className={`p-3 rounded-xl text-center ${isDarkMode ? "bg-white/[0.03]" : "bg-gray-50"}`}>
                  <p className="text-xs font-bold text-white/50">{new Date(m.month + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" })}</p>
                  <p className="text-lg font-black mt-1">{m.avg_temp}°</p>
                  <div className="flex justify-center gap-2 mt-1">
                    <span className="text-[10px] text-red-400">{m.high}°</span>
                    <span className="text-[10px] text-blue-400">{m.low}°</span>
                  </div>
                  <div className={`text-[10px] mt-1 ${isDarkMode ? "text-white/25" : "text-gray-300"}`}>
                    {m.rain_days}r {m.frost_days}f — {m.days_tracked}d
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
